/**
 * Cataloghi di traduzione.
 *
 * L'italiano è il catalogo di riferimento: la sua forma definisce il tipo
 * `TranslationCatalog`, quindi una lingua a cui manchi una chiave non compila.
 * Aggiungere una lingua significa aggiungere un file qui, senza toccare i
 * componenti (§21).
 *
 * Attenzione: i testi dell'informativa e del consenso NON stanno qui. Sono
 * contenuti versionati e immutabili serviti dal backend (§6.7, §6.8): metterli
 * in un catalogo di UI significherebbe poterli cambiare senza versionarli.
 */

export const it = {
  common: {
    appName: 'Event Contact Collector',
    loading: 'Caricamento…',
    retry: 'Riprova',
    cancel: 'Annulla',
    save: 'Salva',
    language: 'Lingua',
  },
  login: {
    title: 'Accedi',
    subtitle: 'Area riservata al personale autorizzato.',
    email: 'E-mail',
    password: 'Password',
    submit: 'Accedi',
    submitting: 'Accesso in corso…',
    failed: 'Credenziali non valide.',
    genericError: 'Non è stato possibile completare l’accesso. Riprova.',
  },
  app: {
    events: 'Fiere',
    logout: 'Esci',
    noEvents: 'Nessuna fiera configurata.',
    eventStatus: {
      DRAFT: 'Bozza',
      ACTIVE: 'Attiva',
      CLOSED: 'Chiusa',
      ARCHIVED: 'Archiviata',
    },
    back: 'Torna alle fiere',
    openEvent: 'Apri la fiera',
    newEvent: 'Nuova fiera',
    policies: 'Informative',
    audit: 'Registro attività',
    users: 'Utenti',
    account: 'Il mio account',
  },
  newEvent: {
    title: 'Nuova fiera',
    name: 'Nome della fiera',
    slug: 'Identificativo breve',
    slugHint: 'solo minuscole, numeri e trattini',
    location: 'Luogo',
    startsAt: 'Data di inizio',
    endsAt: 'Data di fine',
    languages: 'Lingue offerte al visitatore',
    headline: 'Titolo mostrato al visitatore',
    shortDescription: 'Breve descrizione',
    create: 'Crea',
    // La fiera nasce sempre in bozza: attivarla è un atto separato (§19).
    draftNotice:
      'La fiera nasce come bozza e non raccoglie nulla finché non le colleghi un’informativa pubblicata e la attivi.',
    errorSlug: 'Usa solo minuscole, numeri e trattini.',
    errorDates: 'La data di fine non può precedere quella di inizio.',
    errorLanguages: 'Seleziona almeno una lingua.',
  },
  markdown: {
    toolbar: 'Formattazione',
    preview: 'Anteprima',
    hint: 'Formattazione Markdown',
    actions: {
      heading: 'Titolo di sezione',
      bold: 'Grassetto',
      italic: 'Corsivo',
      bulletList: 'Elenco puntato',
      numberedList: 'Elenco numerato',
      link: 'Collegamento',
    },
  },
  policies: {
    title: 'Informative e testi di consenso',
    notices: 'Informative privacy',
    consentTexts: 'Testi di consenso',
    newNotice: 'Nuova informativa',
    version: 'Versione',
    language: 'Lingua',
    noticeTitle: 'Titolo',
    content: 'Testo dell’informativa',
    consentContent: 'Testo che il visitatore spunta',
    draft: 'Bozza',
    published: 'Pubblicata',
    edit: 'Modifica',
    save: 'Salva bozza',
    publish: 'Pubblica',
    cancel: 'Annulla',
    create: 'Crea bozza',
    close: 'Chiudi',
    saveAndPublish: 'Salva e pubblica',
    // Conferma il salvataggio senza spostare l'utente: dopo aver scritto un
    // testo lungo, essere riportati all'elenco è il momento peggiore.
    savedKeepEditing:
      'Bozza salvata. Puoi continuare a modificarla: diventa definitiva solo quando la pubblichi.',
    // È la regola centrale del versionamento (§6.7).
    publishWarning:
      'Dopo la pubblicazione il testo diventa immutabile: per correggerlo si pubblica una nuova versione, e i contatti già raccolti continueranno a riferirsi a questa.',
    publishConfirm:
      'Pubblicare? Da questo momento il testo non sarà più modificabile e potrà essere collegato a una fiera.',
    publishedImmutable:
      'Pubblicata e immutabile. Per modificarla, crea una nuova versione.',
    none: 'Nessuna informativa presente.',
    noneConsent: 'Nessun testo di consenso presente.',
    hash: 'Impronta del contenuto',
    preview: 'Anteprima',
    closePreview: 'Chiudi anteprima',
    // Chiarisce che la bozza è modificabile: è la domanda che ci si pone
    // guardando un documento in attesa di approvazione.
    draftExplain:
      'Bozza: modificabile quante volte serve e non visibile al visitatore. Diventa definitiva e immutabile solo con la pubblicazione.',
    placeholdersFound:
      'Attenzione: restano {n} segnaposto «[DA COMPLETARE — …]» da colmare. Un’informativa incompleta non dovrebbe essere pubblicata: dopo, per correggerla, servirebbe una nuova versione.',
    publishWithPlaceholders:
      'Il testo contiene ancora {n} segnaposto da colmare. Pubblicare comunque?',
    lifecycle: 'Ciclo di vita',
    delete: 'Elimina',
    // Eliminare una bozza non perde nulla: non è mai stata mostrata a nessuno.
    deleteConfirm:
      'Eliminare questa bozza? Non è mai stata mostrata a nessun visitatore, quindi non si perde alcuna prova. L’operazione non è reversibile.',
    retire: 'Ritira',
    retireConfirm:
      'Ritirare questa versione? Resta consultabile e continua a valere come prova per i contatti già raccolti, ma non sarà più selezionabile per nuove fiere.',
    retired: 'Ritirata',
    retiredExplain:
      'Ritirata: non più selezionabile per una fiera. Resta la prova per i contatti raccolti sotto di essa.',
    duplicate: 'Duplica',
    // Duplicare è il gesto naturale del modello: una versione pubblicata non si
    // corregge, se ne pubblica una nuova partendo dal testo precedente (§6.7).
    duplicateExplain:
      'Crea una nuova bozza partendo da questo testo. È il modo previsto per correggere un’informativa già pubblicata: l’originale resta intatto e i contatti raccolti sotto di esso continuano a riferirvisi.',
    duplicatedFrom: 'Bozza creata da {version}. Controlla il numero di versione prima di salvare.',
    lifecycleExplain:
      'Ogni informativa nasce come bozza modificabile. La pubblicazione la rende definitiva: da quel momento non è più correggibile, può essere collegata a una fiera, e i contatti raccolti sotto di essa continueranno a riferirsi a questa versione anche dopo che ne avrai pubblicate di nuove.',
  },
  audit: {
    title: 'Registro attività',
    // Chiarisce subito cosa NON c'è dentro (§6.9).
    intro:
      'Attività amministrative e di sicurezza: chi ha fatto cosa e quando. Non contiene i dati compilati dai visitatori.',
    when: 'Quando',
    what: 'Azione',
    who: 'Autore',
    target: 'Oggetto',
    details: 'Dettagli',
    none: 'Nessuna attività registrata.',
    filterAction: 'Filtra per azione',
    all: 'Tutte',
    system: 'sistema',
  },
  users: {
    title: 'Utenti',
    hint:
      'Gli account del personale. Un OPERATOR legge fiere, contatti e consensi; un ADMIN può anche esportare, cancellare, revocare consensi e pubblicare informative — assegnalo solo a chi deve poterlo fare.',
    emailLabel: 'Indirizzo e-mail',
    roleLabel: 'Ruolo',
    create: 'Crea utente',
    created: 'Utente creato',
    passwordOnce:
      'Questa password compare una volta sola: comunicala alla persona e falla cambiare al primo accesso. Non è recuperabile — se va persa si genera un nuovo reset.',
    roleAdmin: 'Amministratore',
    roleOperator: 'Operatore',
    active: 'Attivo',
    inactive: 'Disattivato',
    deactivate: 'Disattiva',
    activate: 'Riattiva',
    resetPassword: 'Reimposta password',
    makeOperator: 'Rendi operatore',
    makeAdmin: 'Rendi amministratore',
    lastAdminHint:
      'L’ultimo amministratore attivo non può essere disattivato né degradato: nessuno potrebbe più gestire fiere, informative e utenti.',
    empty: 'Nessun utente.',
  },
  account: {
    title: 'Il mio account',
    passwordSection: 'Cambia password',
    passwordHint:
      'Finché usi la password generata da chi ha creato l’account, quella persona la conosce. Cambiala al primo accesso.',
    currentPassword: 'Password attuale',
    newPassword: 'Nuova password',
    confirmPassword: 'Ripeti la nuova password',
    minLength: 'Almeno 12 caratteri.',
    mismatch: 'Le due password non coincidono.',
    submit: 'Cambia password',
    changed:
      'Password cambiata. Tutte le sessioni sono state chiuse, inclusa questa: rientra con la password nuova.',
  },
  eventDetail: {
    callbackSection: 'Casella di ricontatto commerciale',
    callbackHint:
      'Il testo accanto alla casella con cui il visitatore chiede di essere richiamato. Personalizzalo per questa fiera, oppure lascia vuoto per usare quello predefinito.',
    callbackConstraint:
      'Deve descrivere un ricontatto da parte del personale commerciale: è ciò che l’informativa pubblicata dichiara. E non deve somigliare a un consenso marketing, che è una casella separata con un’altra base giuridica.',
    callbackFixed:
      'Comunque sia scritta, la spunta registra una richiesta di ricontatto commerciale, con il suo periodo di conservazione e il suo export. L’etichetta cambia il testo, non la finalità.',
    callbackDefault: 'Testo predefinito:',
    callbackReset: 'Usa il testo predefinito',
    callbackSave: 'Salva etichetta',
    callbackSaved: 'Etichetta aggiornata.',
    itemsSection: 'Voci richiedibili',
    itemsHint:
      'Che cosa il visitatore può chiedere allo stand. Sono contenuto della fiera, non finalità: ogni voce ricade su una finalità già configurata, quindi aggiungerne una non cambia i tempi di conservazione né gli export.',
    itemsNoPreselect:
      'Nessuna voce può essere preselezionata: la scelta è del visitatore. Il modello non prevede quel campo, quindi non è una regola da ricordare.',
    itemsType: 'Tipo',
    itemsIdentifier: 'Identificatore',
    itemsIdentifierHint:
      'Registrato su ogni richiesta raccolta. Non cambia più dopo il salvataggio: modificarlo slegherebbe le richieste già raccolte dalla voce corrente.',
    itemsLabelFor: 'Etichetta',
    itemsAdd: 'Aggiungi voce',
    itemsRemove: 'Rimuovi',
    itemsSave: 'Salva voci',
    itemsSaved: 'Voci aggiornate.',
    itemsAtLeastOne: 'Serve almeno una voce: senza, il modulo non avrebbe nulla da chiedere.',
    itemsMissingLabel: 'Ogni voce richiede un’etichetta nella lingua predefinita della fiera.',
    itemsRemovedWarning:
      'Rimuovere una voce non tocca le richieste già raccolte: restano registrate con il proprio identificatore.',
    bannerSection: 'Immagine della fiera',
    bannerHint:
      'Compare in cima al modulo, sopra i campi. Formati accettati: PNG, JPEG o WebP, fino a 4 MB. Gli SVG non sono accettati perché possono contenere codice eseguibile.',
    bannerAltLabel: 'Descrizione dell’immagine',
    bannerAltHint:
      'Obbligatoria: è ciò che viene letto da chi non può vedere l’immagine. Descrivi che cosa mostra, non che è un banner.',
    bannerFileLabel: 'File dell’immagine',
    bannerUpload: 'Carica immagine',
    bannerReplace: 'Sostituisci immagine',
    bannerRemove: 'Rimuovi immagine',
    bannerNone: 'Nessuna immagine impostata: il modulo mostra solo il testo.',
    bannerPrivacyNote:
      'L’immagine non deve ridurre la visibilità dei controlli privacy: l’altezza è limitata perché la casella di consenso e il link all’informativa restino raggiungibili.',
    bannerMissingAlt: 'Inserisci una descrizione prima di caricare.',
    bannerMissingFile: 'Scegli un file da caricare.',
    policySection: 'Informativa attiva',
    noPolicy: 'Nessuna informativa attiva.',
    // Spiega perché il pulsante di attivazione è disabilitato, invece di
    // lasciare l'utente davanti a un comando inerte.
    policyRequired:
      'Una fiera non può essere attivata senza un’informativa pubblicata: senza, non ci sarebbe una versione immutabile a cui legare la prova di ciò che viene mostrato al visitatore.',
    choosePolicy: 'Scegli un’informativa pubblicata',
    noPublishedPolicy:
      'Nessuna informativa pubblicata disponibile. Va creata e pubblicata prima di attivare la fiera.',
    applyPolicy: 'Imposta come informativa attiva',
    statusSection: 'Stato',
    activate: 'Attiva la fiera',
    close: 'Chiudi la fiera',
    activeHint: 'La fiera accetta nuove richieste dai tablet associati.',
    draftHint: 'La fiera non accetta richieste: è ancora una bozza.',
    closedHint:
      'La fiera non accetta nuove richieste. Le richieste già raccolte offline possono ancora essere sincronizzate.',
    devicesSection: 'Tablet associati',
    noDevices: 'Nessun tablet registrato.',
    deviceLabel: 'Nome del tablet',
    addDevice: 'Registra tablet',
    revokeDevice: 'Revoca',
    revokeConfirm: 'Revocare questo tablet? L’operazione non è reversibile.',
    deviceStatus: {
      PENDING: 'In attesa di associazione',
      ACTIVE: 'Associato',
      REVOKED: 'Revocato',
    },
    tokenTitle: 'Codice di associazione',
    // Il token è mostrato una sola volta: il server ne conserva solo l'hash.
    tokenOnce:
      'Annotalo adesso: non sarà più visibile. Nel sistema resta solo la sua impronta, così una copia del database non consegna tablet funzionanti. Se lo perdi puoi rigenerarlo dall’elenco.',
    reissue: 'Rigenera codice',
    reissueConfirm:
      'Rigenerare il codice? Quello attuale smette subito di funzionare e il tablet tornerà a chiedere l’associazione. Le richieste eventualmente ancora in coda sul tablet restano salvate e si sincronizzano dopo la nuova associazione.',
    tokenCopy: 'Copia',
    tokenCopied: 'Copiato',
    tokenDone: 'Ho annotato il codice',
    lastSeen: 'Ultimo contatto',
    never: 'mai',
    preview: 'Anteprima kiosk',
    contacts: 'Contatti raccolti',
  },
  preview: {
    title: 'Anteprima di ciò che vede il visitatore',
    // L'anteprima è sola lettura per costruzione: non esiste un percorso di
    // invio, quindi non può generare contatti né eventi di consenso.
    banner:
      'Anteprima in sola lettura: il modulo non è inviabile e non registra nulla. Serve a verificare testi, lingua e informativa prima di attivare lo stand.',
    noPolicy:
      'La fiera non ha un’informativa attiva: non c’è ancora nulla da mostrare al visitatore.',
  },
  contacts: {
    title: 'Contatti raccolti',
    none: 'Nessun contatto raccolto per questa fiera.',
    collectedAt: 'Raccolto il',
    requests: 'Ha chiesto',
    marketing: 'Marketing',
    granted: 'Consenso attivo',
    notGranted: 'Nessun consenso',
    since: 'dal',
    history: 'Storico consensi',
    showHistory: 'Mostra storico',
    hideHistory: 'Nascondi storico',
    withdraw: 'Revoca consenso',
    erase: 'Cancella contatto',
    reasonLabel: 'Motivazione (obbligatoria)',
    reasonPlaceholder: 'Es. richiesta ricevuta via e-mail il 12/09/2026',
    confirm: 'Conferma',
    cancel: 'Annulla',
    // La revoca aggiunge un evento: non riscrive la storia (§6.6).
    withdrawExplain:
      'La revoca aggiunge un evento al registro: il consenso precedente resta come prova di ciò che è avvenuto, e l’indirizzo viene iscritto nel registro di opposizione.',
    eraseExplain:
      'La cancellazione rimuove i dati personali e le richieste. Il registro dei consensi conserva l’evento, ormai privo di riferimenti identificativi, come prova di aver ricevuto e onorato la richiesta.',
    eraseConfirmTitle: 'Cancellare questo contatto?',
    withdrawConfirmTitle: 'Revocare il consenso marketing?',
    anonymized: 'Contatto cancellato',
    actionDone: 'Operazione registrata.',
  },
  exports: {
    title: 'Esporta contatti',
    purpose: 'Finalità dell’export',
    // Un export per finalità: mescolarle produrrebbe un file in cui nessuno
    // sa più chi ha autorizzato cosa (§15).
    purposeExplain:
      'Ogni export riguarda una finalità sola. Il file non mescola persone con basi giuridiche diverse, perché a valle non sarebbero più distinguibili.',
    purposes: {
      EMAIL_MARKETING: 'Marketing — solo chi ha prestato il consenso',
      DOCUMENTATION_REQUEST: 'Documentazione richiesta',
      COMMERCIAL_CALLBACK: 'Richieste di ricontatto commerciale',
    },
    purposeHints: {
      EMAIL_MARKETING:
        'Include esclusivamente i contatti con consenso attivo. Chi non lo ha prestato, o lo ha revocato, non compare. Il file riporta indirizzo, data del consenso e versione del testo accettato — non nome, cognome o azienda.',
      DOCUMENTATION_REQUEST:
        'Include chi ha chiesto documentazione o informazioni. La base giuridica è la richiesta stessa dell’interessato, non un consenso marketing.',
      COMMERCIAL_CALLBACK:
        'Include solo chi ha esplicitamente chiesto di essere ricontattato. Non è una lista di marketing.',
    },
    reason: 'Motivo dell’export',
    reasonPlaceholder: 'Es. campagna di follow-up post-fiera',
    download: 'Genera e scarica',
    generating: 'Generazione…',
    // §15 chiede di ricordarlo esplicitamente all'operatore.
    copyWarning:
      'Il file scaricato è una copia contenente dati personali: conservalo solo per il tempo necessario, non inviarlo per e-mail non protetta e cancellalo quando non serve più.',
    done: 'Export generato',
    rows: 'righe',
    empty: 'Nessun contatto corrisponde a questa finalità: il file contiene la sola intestazione.',
    audited: 'L’operazione è stata registrata nel registro attività.',
  },
  kiosk: {
    notConfigured: 'Questo dispositivo non è ancora associato a una fiera.',
    askStaff: 'Chiedi assistenza allo staff.',
    pairTitle: 'Associa il dispositivo',
    pairToken: 'Codice di associazione',
    pairSubmit: 'Associa',
    pairFailed: 'Codice non valido o dispositivo revocato.',
    title: 'Richiedi documentazione',
    firstName: 'Nome',
    lastName: 'Cognome',
    company: 'Azienda',
    email: 'E-mail',
    optional: 'facoltativo',
    required: 'obbligatorio',
    interests: 'Mi interessa',
    callback: 'Desidero essere ricontattato da un commerciale in merito alla mia richiesta.',
    marketingSectionTitle: 'Marketing — facoltativo',
    readPolicy: 'Leggi l’informativa privacy',
    closePolicy: 'Chiudi',
    submit: 'Invia',
    submitting: 'Invio in corso…',
    // Conferma volutamente sobria: non dice né lascia intendere che il
    // visitatore si sia iscritto a qualcosa (§7.4).
    successTitle: 'Richiesta registrata',
    successThanks: 'Grazie.',
    successBody: 'Riceverai la documentazione richiesta all’indirizzo indicato.',
    newRequest: 'Nuova richiesta',
    errorEmail: 'Inserisci un indirizzo e-mail valido.',
    errorRequired: 'Campo obbligatorio.',
    errorNoSelection: 'Seleziona almeno una voce di interesse.',
    // Mostrato solo quando il salvataggio locale è riuscito ma la rete no.
    savedOffline:
      'Richiesta salvata sul dispositivo: verrà sincronizzata appena possibile.',
    // Mostrato quando nemmeno il salvataggio locale è riuscito (§26).
    notSaved: 'Non è stato possibile registrare la richiesta. Chiedi assistenza allo staff.',
    pendingNotice: 'Richieste in attesa di sincronizzazione:',
    exit: 'Esci dalla modalità kiosk',
    operatorSessionTitle: 'Sessione operatore attiva',
    operatorSessionBody:
      'In questo browser è aperta una sessione operatore, che ha la precedenza su quella kiosk: le due identità restano separate di proposito, così un tablet non eredita privilegi amministrativi.',
    operatorSessionHint:
      'Per provare il kiosk apri una finestra anonima (o un altro browser) e vai su /kiosk, oppure esci dalla sessione operatore.',
  },
  requestTypes: {
    DOCUMENT: 'Documentazione tecnica',
    CATALOG: 'Catalogo prodotti',
    PRODUCT_INFO: 'Informazioni su un prodotto',
    COMMERCIAL_CALLBACK: 'Ricontatto da un commerciale',
  },
  errors: {
    offline: 'Connessione assente.',
    unexpected: 'Si è verificato un errore.',
  },
} as const;

/**
 * Forma di un catalogo completo. Derivata dall'italiano: se una lingua non
 * copre tutte le chiavi, l'errore è di compilazione, non un testo mancante
 * scoperto in fiera.
 */
export type TranslationCatalog = {
  readonly [Section in keyof typeof it]: {
    readonly [Key in keyof (typeof it)[Section]]: (typeof it)[Section][Key] extends string
      ? string
      : { readonly [Nested in keyof (typeof it)[Section][Key]]: string };
  };
};

export const en: TranslationCatalog = {
  common: {
    appName: 'Event Contact Collector',
    loading: 'Loading…',
    retry: 'Retry',
    cancel: 'Cancel',
    save: 'Save',
    language: 'Language',
  },
  login: {
    title: 'Sign in',
    subtitle: 'Restricted to authorised staff.',
    email: 'Email',
    password: 'Password',
    submit: 'Sign in',
    submitting: 'Signing in…',
    failed: 'Invalid credentials.',
    genericError: 'Sign-in could not be completed. Please try again.',
  },
  app: {
    events: 'Fairs',
    logout: 'Sign out',
    noEvents: 'No fair configured.',
    eventStatus: {
      DRAFT: 'Draft',
      ACTIVE: 'Active',
      CLOSED: 'Closed',
      ARCHIVED: 'Archived',
    },
    back: 'Back to fairs',
    openEvent: 'Open fair',
    newEvent: 'New fair',
    policies: 'Privacy notices',
    audit: 'Activity log',
    users: 'Users',
    account: 'My account',
  },
  newEvent: {
    title: 'New fair',
    name: 'Fair name',
    slug: 'Short identifier',
    slugHint: 'lowercase letters, digits and hyphens only',
    location: 'Location',
    startsAt: 'Start date',
    endsAt: 'End date',
    languages: 'Languages offered to visitors',
    headline: 'Headline shown to visitors',
    shortDescription: 'Short description',
    create: 'Create',
    draftNotice:
      'The fair starts as a draft and collects nothing until you link a published privacy notice and activate it.',
    errorSlug: 'Use lowercase letters, digits and hyphens only.',
    errorDates: 'The end date cannot precede the start date.',
    errorLanguages: 'Select at least one language.',
  },
  markdown: {
    toolbar: 'Formatting',
    preview: 'Preview',
    hint: 'Markdown formatting',
    actions: {
      heading: 'Section heading',
      bold: 'Bold',
      italic: 'Italic',
      bulletList: 'Bulleted list',
      numberedList: 'Numbered list',
      link: 'Link',
    },
  },
  policies: {
    title: 'Privacy notices and consent texts',
    notices: 'Privacy notices',
    consentTexts: 'Consent texts',
    newNotice: 'New notice',
    version: 'Version',
    language: 'Language',
    noticeTitle: 'Title',
    content: 'Notice text',
    consentContent: 'Text the visitor ticks',
    draft: 'Draft',
    published: 'Published',
    edit: 'Edit',
    save: 'Save draft',
    publish: 'Publish',
    cancel: 'Cancel',
    create: 'Create draft',
    close: 'Close',
    saveAndPublish: 'Save and publish',
    savedKeepEditing:
      'Draft saved. You can keep editing it: it becomes final only when you publish.',
    publishWarning:
      'Once published the text becomes immutable: to correct it you publish a new version, and contacts already collected keep referring to this one.',
    publishConfirm:
      'Publish? From now on the text cannot be changed and can be linked to a fair.',
    publishedImmutable: 'Published and immutable. To change it, create a new version.',
    none: 'No privacy notice yet.',
    noneConsent: 'No consent text yet.',
    hash: 'Content fingerprint',
    preview: 'Preview',
    closePreview: 'Close preview',
    draftExplain:
      'Draft: editable as many times as needed and not visible to visitors. It becomes final and immutable only on publication.',
    placeholdersFound:
      'Warning: {n} «[DA COMPLETARE — …]» placeholders still need filling. An incomplete notice should not be published: afterwards, correcting it would require a new version.',
    publishWithPlaceholders: 'The text still contains {n} placeholders. Publish anyway?',
    lifecycle: 'Life cycle',
    delete: 'Delete',
    deleteConfirm:
      'Delete this draft? It was never shown to any visitor, so no evidence is lost. This cannot be undone.',
    retire: 'Retire',
    retireConfirm:
      'Retire this version? It stays readable and keeps standing as evidence for contacts already collected, but it will no longer be selectable for new fairs.',
    retired: 'Retired',
    retiredExplain:
      'Retired: no longer selectable for a fair. It remains the evidence for contacts collected under it.',
    duplicate: 'Duplicate',
    duplicateExplain:
      'Creates a new draft from this text. It is the intended way to correct an already published notice: the original stays intact and contacts collected under it keep referring to it.',
    duplicatedFrom: 'Draft created from {version}. Check the version number before saving.',
    lifecycleExplain:
      'Every notice starts as an editable draft. Publishing makes it final: from then on it cannot be corrected, it can be linked to a fair, and contacts collected under it keep referring to this version even after newer ones are published.',
  },
  audit: {
    title: 'Activity log',
    intro:
      'Administrative and security activity: who did what and when. It does not contain data entered by visitors.',
    when: 'When',
    what: 'Action',
    who: 'Actor',
    target: 'Target',
    details: 'Details',
    none: 'No activity recorded.',
    filterAction: 'Filter by action',
    all: 'All',
    system: 'system',
  },
  users: {
    title: 'Users',
    hint:
      'Staff accounts. An OPERATOR reads fairs, contacts and consents; an ADMIN can also export, erase, withdraw consent and publish notices — grant it only to people who need it.',
    emailLabel: 'E-mail address',
    roleLabel: 'Role',
    create: 'Create user',
    created: 'User created',
    passwordOnce:
      'This password is shown once: pass it on and have it changed at first sign-in. It cannot be recovered — if lost, generate a new reset.',
    roleAdmin: 'Administrator',
    roleOperator: 'Operator',
    active: 'Active',
    inactive: 'Deactivated',
    deactivate: 'Deactivate',
    activate: 'Reactivate',
    resetPassword: 'Reset password',
    makeOperator: 'Make operator',
    makeAdmin: 'Make administrator',
    lastAdminHint:
      'The last active administrator cannot be deactivated or demoted: nobody would be left able to manage fairs, notices and users.',
    empty: 'No users.',
  },
  account: {
    title: 'My account',
    passwordSection: 'Change password',
    passwordHint:
      'While you use the password generated by whoever created the account, that person knows it. Change it at first sign-in.',
    currentPassword: 'Current password',
    newPassword: 'New password',
    confirmPassword: 'Repeat the new password',
    minLength: 'At least 12 characters.',
    mismatch: 'The two passwords do not match.',
    submit: 'Change password',
    changed:
      'Password changed. All sessions were closed, including this one: sign in again with the new password.',
  },
  eventDetail: {
    callbackSection: 'Sales callback checkbox',
    callbackHint:
      'The text next to the checkbox visitors use to ask to be contacted. Customise it for this event, or leave empty to use the default.',
    callbackConstraint:
      'It must describe a callback by sales staff: that is what the published privacy notice declares. And it must not resemble a marketing consent, which is a separate checkbox with a different legal basis.',
    callbackFixed:
      'Whatever it says, ticking it records a sales callback request, with its own retention period and its own export. The label changes the wording, not the purpose.',
    callbackDefault: 'Default text:',
    callbackReset: 'Use the default text',
    callbackSave: 'Save label',
    callbackSaved: 'Label updated.',
    itemsSection: 'Requestable items',
    itemsHint:
      'What visitors can ask for at the stand. These are event content, not purposes: each item maps to an already configured purpose, so adding one changes neither retention nor exports.',
    itemsNoPreselect:
      'No item can be pre-ticked: the choice is the visitor’s. The model has no such field, so it is not a rule to remember.',
    itemsType: 'Type',
    itemsIdentifier: 'Identifier',
    itemsIdentifierHint:
      'Recorded on every request collected. It cannot change after saving: changing it would detach already collected requests from the current item.',
    itemsLabelFor: 'Label',
    itemsAdd: 'Add item',
    itemsRemove: 'Remove',
    itemsSave: 'Save items',
    itemsSaved: 'Items updated.',
    itemsAtLeastOne: 'At least one item is required: without it the form would have nothing to ask for.',
    itemsMissingLabel: 'Every item needs a label in the event’s default language.',
    itemsRemovedWarning:
      'Removing an item does not affect requests already collected: they stay recorded with their own identifier.',
    bannerSection: 'Event image',
    bannerHint:
      'Shown at the top of the form, above the fields. Accepted formats: PNG, JPEG or WebP, up to 4 MB. SVG files are not accepted because they can contain executable code.',
    bannerAltLabel: 'Image description',
    bannerAltHint:
      'Required: it is what people who cannot see the image will hear. Describe what it shows, not that it is a banner.',
    bannerFileLabel: 'Image file',
    bannerUpload: 'Upload image',
    bannerReplace: 'Replace image',
    bannerRemove: 'Remove image',
    bannerNone: 'No image set: the form shows text only.',
    bannerPrivacyNote:
      'The image must not reduce the visibility of the privacy controls: its height is capped so the consent checkbox and the notice link stay reachable.',
    bannerMissingAlt: 'Enter a description before uploading.',
    bannerMissingFile: 'Choose a file to upload.',
    policySection: 'Active privacy notice',
    noPolicy: 'No active privacy notice.',
    policyRequired:
      'A fair cannot be activated without a published privacy notice: without one there would be no immutable version to tie the record of what the visitor was shown to.',
    choosePolicy: 'Choose a published notice',
    noPublishedPolicy:
      'No published privacy notice available. One must be created and published before activating the fair.',
    applyPolicy: 'Set as active notice',
    statusSection: 'Status',
    activate: 'Activate fair',
    close: 'Close fair',
    activeHint: 'The fair accepts new requests from paired tablets.',
    draftHint: 'The fair accepts no requests: it is still a draft.',
    closedHint:
      'The fair accepts no new requests. Requests already collected offline can still be synchronised.',
    devicesSection: 'Paired tablets',
    noDevices: 'No tablet registered.',
    deviceLabel: 'Tablet name',
    addDevice: 'Register tablet',
    revokeDevice: 'Revoke',
    revokeConfirm: 'Revoke this tablet? This cannot be undone.',
    deviceStatus: {
      PENDING: 'Awaiting pairing',
      ACTIVE: 'Paired',
      REVOKED: 'Revoked',
    },
    tokenTitle: 'Pairing code',
    tokenOnce:
      'Write it down now: it will not be shown again. Only its fingerprint is kept, so a copy of the database does not hand over working tablets. If lost, you can reissue it from the list.',
    reissue: 'Reissue code',
    reissueConfirm:
      'Reissue the code? The current one stops working immediately and the tablet will ask to pair again. Any requests still queued on the tablet stay saved and sync after re-pairing.',
    tokenCopy: 'Copy',
    tokenCopied: 'Copied',
    tokenDone: 'I have written the code down',
    lastSeen: 'Last seen',
    never: 'never',
    preview: 'Kiosk preview',
    contacts: 'Collected contacts',
  },
  preview: {
    title: 'Preview of what the visitor sees',
    banner:
      'Read-only preview: the form cannot be submitted and records nothing. Use it to check texts, language and privacy notice before opening the stand.',
    noPolicy: 'The fair has no active privacy notice: there is nothing to show the visitor yet.',
  },
  contacts: {
    title: 'Collected contacts',
    none: 'No contact collected for this fair.',
    collectedAt: 'Collected on',
    requests: 'Requested',
    marketing: 'Marketing',
    granted: 'Consent active',
    notGranted: 'No consent',
    since: 'since',
    history: 'Consent history',
    showHistory: 'Show history',
    hideHistory: 'Hide history',
    withdraw: 'Withdraw consent',
    erase: 'Erase contact',
    reasonLabel: 'Reason (required)',
    reasonPlaceholder: 'e.g. request received by email on 12/09/2026',
    confirm: 'Confirm',
    cancel: 'Cancel',
    withdrawExplain:
      'Withdrawal appends an event to the log: the earlier consent remains as evidence of what happened, and the address is added to the suppression registry.',
    eraseExplain:
      'Erasure removes personal data and requests. The consent log keeps the event, now without identifying references, as evidence that the request was received and honoured.',
    eraseConfirmTitle: 'Erase this contact?',
    withdrawConfirmTitle: 'Withdraw marketing consent?',
    anonymized: 'Contact erased',
    actionDone: 'Action recorded.',
  },
  exports: {
    title: 'Export contacts',
    purpose: 'Export purpose',
    purposeExplain:
      'Each export covers a single purpose. The file never mixes people with different legal bases, because downstream they could no longer be told apart.',
    purposes: {
      EMAIL_MARKETING: 'Marketing — only those who gave consent',
      DOCUMENTATION_REQUEST: 'Requested documentation',
      COMMERCIAL_CALLBACK: 'Sales callback requests',
    },
    purposeHints: {
      EMAIL_MARKETING:
        'Includes only contacts with active consent. Anyone who did not give it, or withdrew it, is absent. The file carries the address, the consent date and the version of the text accepted — not name, surname or company.',
      DOCUMENTATION_REQUEST:
        'Includes those who requested documentation or information. The legal basis is the request itself, not a marketing consent.',
      COMMERCIAL_CALLBACK:
        'Includes only those who explicitly asked to be contacted. This is not a marketing list.',
    },
    reason: 'Reason for the export',
    reasonPlaceholder: 'e.g. post-fair follow-up campaign',
    download: 'Generate and download',
    generating: 'Generating…',
    copyWarning:
      'The downloaded file is a copy containing personal data: keep it only as long as needed, do not send it over unprotected email, and delete it when no longer required.',
    done: 'Export generated',
    rows: 'rows',
    empty: 'No contact matches this purpose: the file contains only the header.',
    audited: 'The operation has been recorded in the activity log.',
  },
  kiosk: {
    notConfigured: 'This device is not linked to a fair yet.',
    askStaff: 'Please ask a member of staff.',
    pairTitle: 'Pair this device',
    pairToken: 'Pairing code',
    pairSubmit: 'Pair',
    pairFailed: 'Invalid code, or the device has been revoked.',
    title: 'Request documentation',
    firstName: 'First name',
    lastName: 'Last name',
    company: 'Company',
    email: 'Email',
    optional: 'optional',
    required: 'required',
    interests: 'I am interested in',
    callback: 'I would like a sales representative to contact me about my request.',
    marketingSectionTitle: 'Marketing — optional',
    readPolicy: 'Read the privacy notice',
    closePolicy: 'Close',
    submit: 'Send',
    submitting: 'Sending…',
    successTitle: 'Request recorded',
    successThanks: 'Thank you.',
    successBody: 'You will receive the requested documentation at the address provided.',
    newRequest: 'New request',
    errorEmail: 'Please enter a valid email address.',
    errorRequired: 'This field is required.',
    errorNoSelection: 'Please select at least one item.',
    savedOffline: 'Request saved on this device: it will be synchronised as soon as possible.',
    notSaved: 'The request could not be recorded. Please ask a member of staff.',
    pendingNotice: 'Requests awaiting synchronisation:',
    exit: 'Exit kiosk mode',
    operatorSessionTitle: 'Staff session active',
    operatorSessionBody:
      'A staff session is open in this browser and takes precedence over the kiosk one: the two identities stay separate by design, so a tablet never inherits administrative privileges.',
    operatorSessionHint:
      'To try the kiosk, open a private window (or another browser) and go to /kiosk, or sign out of the staff session.',
  },
  requestTypes: {
    DOCUMENT: 'Technical documentation',
    CATALOG: 'Product catalogue',
    PRODUCT_INFO: 'Information about a product',
    COMMERCIAL_CALLBACK: 'Callback from sales',
  },
  errors: {
    offline: 'No connection.',
    unexpected: 'Something went wrong.',
  },
};

/**
 * Registro delle lingue disponibili nella UI.
 * Estenderlo è l'unico passo necessario per aggiungere una lingua.
 */
export const catalogs: Readonly<Record<string, TranslationCatalog>> = { it, en };

export const AVAILABLE_UI_LANGUAGES = Object.keys(catalogs);
